/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Range, Root, Track } from "@radix-ui/react-slider";
import type { ComponentProps } from "react";

import {
  colors,
  constantSemanticColors,
  numericPercentages,
  numericPixels,
  radius,
  spacing,
  useEmotionColor,
} from "../../../../../global/emotion";
import type { Color } from "../../../../types/color.emotion.types";

export type SliderProps = Omit<
  ComponentProps<typeof Root>,
  "value" | "defaultValue"
> & {
  value?: number;
  defaultValue?: number;
  size?: "small" | "large" | "xl";
  onColor?: boolean;
  color?: Color;
  css?: Interpolation;
  trackCSS?: Interpolation;
  rangeCSS?: Interpolation;
};

const rangeStyle: Interpolation = {
  backgroundColor: colors.white,
  borderRadius: radius.full,
  display: "block",
  height: numericPercentages[100],
  position: "absolute",
};
const rangeOnColorStyle: Interpolation = {
  backgroundColor: constantSemanticColors.onColorVeryHigh,
};
const rootStyle: Interpolation = {
  display: "block",
  height: spacing.s,
  width: numericPercentages[100],
};
const rootXLStyle: Interpolation = {
  height: spacing.m,
};
const smallRangeStyle: Interpolation = {
  minWidth: spacing.s,
};
const smallRootStyle: Interpolation = {
  width: numericPixels[240],
};
const trackStyle: Interpolation = {
  backgroundColor: constantSemanticColors.onColorHigh,
  borderRadius: radius.full,
  display: "block",
  height: numericPercentages[100],
  position: "relative",
  width: numericPercentages[100],
  overflow: "hidden",
};
const disabledTrackStyle: Interpolation = {
  cursor: "default",
};
const dynamicColorStyle: Interpolation<string> = (color) => ({
  backgroundColor: color,
});

export const Slider = ({
  size = "large",
  value = 0,
  step = 1,
  defaultValue = 0,
  orientation = "horizontal",
  onColor,
  color,
  disabled,
  css,
  rangeCSS = {},
  trackCSS = {},
  ...props
}: SliderProps) => {
  const getColor = useEmotionColor();
  return (
    <Root
      value={[isNaN(value) ? 0 : value]}
      defaultValue={[defaultValue]}
      step={step}
      orientation={orientation}
      disabled={disabled}
      css={[
        rootStyle,
        size === "small" ? smallRootStyle : null,
        size === "xl" ? rootXLStyle : null,
        css,
      ]}
      {...props}
    >
      <Track css={[trackStyle, disabled ? disabledTrackStyle : null, trackCSS]}>
        <Range
          css={[
            rangeStyle,
            size === "small" ? smallRangeStyle : null,
            onColor ? rangeOnColorStyle : null,
            color ? dynamicColorStyle(getColor(color)) : null,
            rangeCSS,
          ]}
        />
      </Track>
    </Root>
  );
};
